<template>
    <div class="ph-cards-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="ph-cards-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #item.record_id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
            </template>

            <template #item.card="elem">
                <div v-if="elem.item.number != null">{{ elem.item.number }}</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.updated_at != null">{{ useDataFormatWithTime(elem.item.updated_at) }}</div>
            </template>

            <template #bottom>
                <div v-if="items.length === 0 && (isAdmin || isAdminColian)" class="ph-cards-table__btn-wrapper">
                    <KButton
                        id="add-card"
                        :label="$t('salesRep.addCard')"
                        secondary
                        uppercase
                        @click="openDialog"
                    />
                </div>
            </template>
        </v-data-table-server>

        <KDialog
            :is-open-dialog-action="isOpenDialog"
            :is-close-dialog-action="isCloseDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="ph-cards-table__dialog-title">{{ $t('salesRep.addCard') }}</div>
                    <div class="ph-cards-table__dialog-input-wrapper">
                        <KTextField
                            id="card-value"
                            v-model="cardNumber"
                            name="cardNumber"
                            label=""
                            hide-details
                            :placeholder="$t('salesRep.cardNumberForm')"
                        />
                    </div>
                    <div class="ph-cards-table__btn-dialog-wrapper">
                        <KButton
                            id="cancel-card"
                            :label="$t('salesRep.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="add-card"
                            :label="$t('salesRep.add')"
                            type="submit"
                            secondary
                            :loading="loadingBtn"
                            @click="addAction"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { defineProps, ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import { useDataFormatWithTime } from '@/hooks/DataHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { toast } from 'vue3-toastify';
import UserApi from '@/api/v1/UserApi';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type {
    ISalesRepDetailsResponse, ISalesRepItemCardResponse,
} from '@/models/SalesRepModel';
import KButton from '@/components/atoms/KButton.vue';
import KDialog from '@/components/atoms/KDialog.vue';
import KTextField from '@/components/atoms/KTextField.vue';

interface IOwnProps {
    userDetails: ISalesRepDetailsResponse | null
    loading: boolean
    handleAddCardAction: () => void
}
interface IProps extends IOwnProps {
}
const { isAdmin, isAdminColian } = storeToRefs(useUserStore());
const props = defineProps<IProps>();
const headers = ref<ITableHeader[]>([
    {
        title: t('salesRep.recordIdLogins'),
        align: 'start',
        sortable: false,
        key: 'record_id',
        width: '150px',
        minWidth: '150px',
    },
    {
        title: t('salesRep.cardNumberHeader'),
        align: 'center',
        sortable: false,
        key: 'card',
        width: '',
        minWidth: '160px',
    },
    {
        title: t('salesRep.dataLogins'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
        minWidth: '160px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<ISalesRepItemCardResponse[]>([]);
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
};
watch(() => props.userDetails, (newValue, oldValue) => {
    if (newValue.card != null) {
        items.value = [];
        items.value.push(props.userDetails.card);
    }
},{
    deep: true,
});

const isOpenDialog = ref<boolean>(false);
const isCloseDialog = ref<boolean>(true);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDialog.value = false;
    isCloseDialog.value = true;
};
const openDialog = () => {
    isOpenDialog.value = true;
    isCloseDialog.value = false;
};
const loadingBtn = ref<boolean>(false);
const cardNumber = ref<string>('');
const userApi = new UserApi();
const addAction = async() => {
    loadingBtn.value = true;
    if (cardNumber.value.length !== 16) {
        toast.warning(`${t('salesRep.toast.warningLengthAddCard')}`);
        loadingBtn.value = false;
        return;
    }
    try {
        const result = await userApi.addCard({
            user_id: +props.userDetails.id,
            number: cardNumber.value,
        });
        if (!result.isSuccess) {
            toast.error(`${t('salesRep.toast.errorAddCard')}`);
            loadingBtn.value = false;
            return;
        }
        toast.success(`${t('salesRep.toast.successAddCard')}`);
        closeDialogWhenNoTargetPressed();
        props.handleAddCardAction();
    } catch (e) {
        toast.error(`${t('salesRep.toast.errorAddCard')}`);
        loadingBtn.value = false;
        return;
    }
    loadingBtn.value = false;
};
</script>

<style scoped lang="scss">
.ph-cards-table {
    &__loading {
        opacity: 0.6;
    }

    &__btn-wrapper {
        display: flex;
        justify-content: flex-end;
        padding: 10.5px;
        background-color: $primary-900;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &__dialog-input-wrapper {
        margin-top: 10px;

        &:deep(.k-input) {
            background: $white;
            border-radius: 6px;
        }
    }

    &:deep(.v-data-table th) {
        background-color: $primary-900 !important;
    }

    &:deep(.v-data-table td) {
        &:first-child {
            background: $primary-900 !important;
        }
    }

    &:deep(.v-data-table tr:hover) {
        background-color: $secondary-400 !important;
        transition: 0s !important;

        td {
            background: $secondary-400 !important;
            color: $black !important;
        }
    }

    &:deep(.v-data-table-footer) {
        background-color: $primary-900 !important;
    }
}
</style>
